import { SampleDrawKitStep } from "@app/components/sampleDraw/steps";
import React, { FC } from "react";
import { ScheduleBloodDrawScreens } from "../scheduleBloodDraw";
import { DrawRequestContext, KitContext } from "@app/provider";
import { LoadingContext } from "@natera/platform/lib/components/context";
import { DrawRequestFlow } from "@app/provider/drawRequest";

import "./kitDetails.scss";

type KitDetailsProps = {
  setBloodDrawScreen: React.Dispatch<
    React.SetStateAction<ScheduleBloodDrawScreens>
  >;
  token: string;
  flow: DrawRequestFlow;
};

const KitDetails: FC<KitDetailsProps> = ({
  setBloodDrawScreen,
  token,
  flow,
}) => {
  const {
    verifiedSampleDrawData,
    verifySampleDrawWithoutLogging,
    checkSampleDraw,
    isLoading,
  } = React.useContext(DrawRequestContext);
  const { setSkipKitShipping, setSkipLabStep, skipKitShipping } =
    React.useContext(KitContext);

  const orderUidPublic =
    verifiedSampleDrawData?.sampleInfo.sample?.orderUidPublic;
  const testUid = verifiedSampleDrawData?.sampleInfo.sample?.testUid;
  const profileAddresses =
    verifiedSampleDrawData?.profileInfo.profile?.addresses;
  const dateOfBirth = verifiedSampleDrawData?.profileInfo.profile?.dateOfBirth;

  const handleKitShippingStepPassed = async () => {
    if (dateOfBirth) {
      // Re-verify before the stepper re-mints the DOB-proof intent; forward the
      // flow so the public MP path stays labeled public_blood_draw (not the
      // guest default) — otherwise this later mint overwrites the match-step one.
      await verifySampleDrawWithoutLogging(
        token,
        dateOfBirth,
        undefined,
        flow === DrawRequestFlow.PUBLIC_MP
          ? "public_blood_draw"
          : "guest_blood_draw"
      );
    }
    setBloodDrawScreen(
      ScheduleBloodDrawScreens.MOBILE_PHLEBOTOMY_STEPPER_SCREEN
    );
  };

  const onBack = async () => {
    setBloodDrawScreen(ScheduleBloodDrawScreens.PERSONAL_INFO_SCREEN);
  };

  const handleCheckSample = async (
    orderUidPublic: string,
    testUid: string,
    token: string
  ) => {
    const response = await checkSampleDraw(orderUidPublic, testUid, token);
    if (!response?.needsKitShippingStep) {
      handleKitShippingStepPassed();
    }
    flow === DrawRequestFlow.PUBLIC_MP
      ? setSkipLabStep(true)
      : setSkipLabStep(Boolean(response?.skipLabStep));
    setSkipKitShipping(!response?.needsKitShippingStep);
  };

  React.useEffect(() => {
    if (skipKitShipping) {
      setBloodDrawScreen(
        ScheduleBloodDrawScreens.MOBILE_PHLEBOTOMY_STEPPER_SCREEN
      );
    }
    if (orderUidPublic && testUid && !skipKitShipping) {
      handleCheckSample(orderUidPublic, testUid, token);
    }
  }, []);

  return (
    <LoadingContext isLoading={isLoading}>
      <div className="guest-kit-details">
        <div className="guest-kit-details__content">
          <SampleDrawKitStep
            onSubmit={handleKitShippingStepPassed}
            onBack={flow === DrawRequestFlow.PUBLIC_MP ? undefined : onBack}
            orderUidPublic={orderUidPublic}
            testUid={testUid}
            profileAddresses={profileAddresses}
            token={token}
            isPublicMpFlow={flow === DrawRequestFlow.PUBLIC_MP}
          />
        </div>
      </div>
    </LoadingContext>
  );
};

export default KitDetails;
